import styled from '@emotion/styled';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import type { ComponentProps, FunctionComponent } from 'react';

export const Backdrop: FunctionComponent<ComponentProps<typeof motion.div>> = ({
    animate = { opacity: 1 },
    className,
    initial = { opacity: 0 },
    ...props
}) => (
    <motion.div
        animate={animate}
        className={clsx('fixed inset-0 z-[1] bg-edit-mode-backdrop', className)}
        initial={initial}
        {...props}
    />
);

export const ContentContainer = styled(motion.div)`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: ${({ theme }) => theme.spacing['4xl']};
`;

// Below this width the skills column stacks under the content sections so long skill
// names and proficiency scales stay inside the drawer instead of clipping.
// Matches Tailwind's `@lg` container breakpoint.
const CONTENT_SECTIONS_TWO_COLUMN_MIN_WIDTH = '32rem';

export type ContentSectionsColumns = 'one' | 'responsive' | 'two';

const twoColumns = 'minmax(0px, 1fr) minmax(0px, 1fr)';

const ContentSectionsGrid = styled('div', {
    shouldForwardProp: (property) => property !== '$columns',
})<{ $columns: ContentSectionsColumns }>`
    background-color: var(--color-surface-raised);
    box-sizing: border-box;
    display: grid;
    grid-template-columns: ${({ $columns }) => ($columns === 'two' ? twoColumns : 'minmax(0px, 1fr)')};
    gap: ${({ theme }) => theme.spacing.xl};
    align-items: flex-start;
    justify-content: flex-start;
    position: relative;
    width: 100%;
    max-width: 100%;
    margin-top: ${({ theme }) => theme.spacing.xxl};

    @container (min-width: ${CONTENT_SECTIONS_TWO_COLUMN_MIN_WIDTH}) {
        grid-template-columns: ${({ $columns }) => ($columns === 'one' ? 'minmax(0px, 1fr)' : twoColumns)};
    }
`;

// `responsive` switches on the width of the surface that renders it, not the viewport, so a
// profile in a drawer and the same profile on its standalone page each get the column count
// their own width allows. `two` is for side-by-side comparisons whose columns must stay
// aligned with the headers above them at every width. `one` stacks them at every width.
export const ContentSections: FunctionComponent<
    ComponentProps<'div'> & { readonly columns?: ContentSectionsColumns }
> = ({ columns = 'responsive', ...props }) => (
    <div className="@container w-full min-w-0">
        <ContentSectionsGrid $columns={columns} {...props} />
    </div>
);

export const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing['3xl']};
`;

export const SectionHeader = styled.div`
    box-sizing: border-box;
    font-size: ${({ theme }) => theme.typography.body.M.medium.fontSize};
    font-weight: ${({ theme }) => theme.typography.body.M.medium.fontWeight};
    line-height: ${({ theme }) => theme.typography.body.M.medium.lineHeight};
    display: flex;
    flex-direction: row;
    margin-bottom: ${({ theme }) => theme.spacing.l};
    gap: ${({ theme }) => theme.spacing.s};
    align-items: center;
    justify-content: flex-start;
    padding: 0;
    position: relative;
    flex-shrink: 0;
    color: var(--color-text-primary);
`;

export const Wrapper = styled.div`
    display: flex;
    width: 100%;
    min-width: 0;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing['3xl']};
`;

export const FlexRowContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing.xl};
`;

// The old world is wrapping the page with a "white dummy card",
// in the new world we have to remove the background and padding/margins.
export const GrayWrapper = styled.div`
    background-color: var(--color-surface-subtle);
    border-radius: 0;
    height: 100%;
`;

export const GrayWrapperInner = styled.div`
    padding-left: ${({ theme }) => theme.spacing.m};
`;
